import React from "react";
import * as LogRocket from "logrocket";

import { Route, Switch } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ErrorBoundary } from "react-error-boundary";
import { Box, CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import "./app.css";
import { isProduction } from "./constants";
import * as Page from "./pages";
import { Private, SideNav, AppState, ErrorBoundaryFallback } from "./components/ui";
import {
  FeedProvider,
  CustomerProvider,
  DiscountsProvider,
  SearchCustomersProvider,
  IntercomProvider,
  RithmicAccountsProvider,
  DiscountCodesLogsProvider,
  AgentsLogsHistoryProvider,
  EvaluationsProvider,
  useAdminProviderDataContext,
  ComplianceSearchProvider,
  PassersProvider,
  useAuthContext,
} from "./context";
import FinancialsProvider from "./context/PassersProvider";

if (isProduction) {
  LogRocket.init("earn2trade/e2t-admin-panel");
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#1288e5",
    },
    secondary: {
      main: "#e53935",
    },
    error: {
      main: "#e53935",
    },
    warning: {
      main: "#e18d10",
    },
  },
  components: {
    MuiTabs: {
      styleOverrides: {
        root: {
          marginBottom: 5,
          backgroundColor: "#f2f2f2",
          "& .MuiButtonBase-root.Mui-selected": {
            color: "#000",
          },
        },
        indicator: {
          backgroundColor: "#1288e5",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            marginLeft: 0,
            marginTop: -3,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          padding: "0 5px",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        color: "primary",
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: "primary",
      },
    },
  },
});

const App = (): React.ReactElement => {
  const { token } = useAuthContext();
  const { user } = useAdminProviderDataContext();
  const { allowed_features } = user;

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <CssBaseline />
        <Box display="flex" flexDirection="row" justifyContent="end">
          <SideNav />
          <Box p={2} sx={{ width: token ? "calc(100% - 221px)" : "100%" }}>
            <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
              <PassersProvider>
                <SearchCustomersProvider>
                  <DiscountsProvider>
                    <AgentsLogsHistoryProvider>
                      <DiscountCodesLogsProvider>
                        <RithmicAccountsProvider>
                          <FeedProvider>
                            <AppState />
                            <Switch>
                              <Private path="/" exact>
                                {(allowed_features.access_activity_feed_page ||
                                  allowed_features.read_agents_log) && <Page.Feed />}
                              </Private>

                              {allowed_features.access_to_helios_admin_page && (
                                <Private path="/helios-admin">
                                  <Page.HeliosAdmin />
                                </Private>
                              )}

                              {allowed_features.access_customers_page && (
                                <Private path="/customers">
                                  <Page.CustomersTabs />
                                </Private>
                              )}

                              <Route path="/login">
                                <Page.Login />
                              </Route>

                              {allowed_features.access_financials_info && (
                                <Private path="/financials">
                                  <FinancialsProvider>
                                    <Page.Financials />
                                  </FinancialsProvider>
                                </Private>
                              )}

                              {allowed_features.access_rithmic_historical_orders && (
                                <Private path="/rithmicHistoricalOrders">
                                  <Page.RithmicHistoricalOrders />
                                </Private>
                              )}

                              {allowed_features.access_agents_page && (
                                <Private path="/agent/:id">
                                  <Page.Agent />
                                </Private>
                              )}
                              {allowed_features.access_agents_page && (
                                <Private path="/agents">
                                  <Page.Agents />
                                </Private>
                              )}

                              <Private path="/xlsx-customers-data">
                                <Page.ExcelParser />
                              </Private>

                              {allowed_features.access_manage_affiliate_accounts && (
                                <Private path="/affiliate/:id">
                                  <Page.Affiliate />
                                </Private>
                              )}

                              {allowed_features.access_manage_affiliate_accounts && (
                                <Private path="/affiliates">
                                  <Page.Affiliates />
                                </Private>
                              )}

                              {allowed_features.access_to_evaluations_tab && (
                                <Private path="/evaluations">
                                  <EvaluationsProvider>
                                    <Page.Evaluations />
                                  </EvaluationsProvider>
                                </Private>
                              )}

                              {allowed_features.access_funded_traders && (
                                <Private exact path="/fundedTraders">
                                  <Page.FundedTraders />
                                </Private>
                              )}

                              {allowed_features.access_funded_compliance && (
                                <Private exact path="/fundedCompliance">
                                  <Page.FundedCompliance />
                                </Private>
                              )}

                              {allowed_features.access_funded_compliance && (
                                <Private exact path="/fundedCompliance/:id">
                                  <Page.FundedComplianceProfile />
                                </Private>
                              )}

                              {allowed_features.access_withdrawals && (
                                <Private path="/withdrawalRequests">
                                  <Page.Withdrawals />
                                </Private>
                              )}

                              {allowed_features.access_funded_traders && (
                                <Private path="/fundedTraders/accounts-id/:id/funded-trader-id/:funded_trader_id">
                                  <CustomerProvider>
                                    <IntercomProvider>
                                      <Page.FundedTrader />
                                    </IntercomProvider>
                                  </CustomerProvider>
                                </Private>
                              )}

                              {allowed_features.access_to_discounts_tab && (
                                <Private exact path="/discounts">
                                  <Page.Discounts />
                                </Private>
                              )}
                              {allowed_features.access_to_discounts_tab && (
                                <Private exact path="/discount/:id">
                                  <Page.Discount />
                                </Private>
                              )}

                              {allowed_features.access_journeys && (
                                <Private exact path="/journeys">
                                  <Page.Journeys />
                                </Private>
                              )}

                              {allowed_features.access_journeys && (
                                <Private exact path="/journeys/:id">
                                  <Page.Journey />
                                </Private>
                              )}

                              {allowed_features.access_rithmic_accounts && (
                                <Private exact path="/rithmicAccounts">
                                  {allowed_features.access_rithmic_accounts && (
                                    <Page.RithmicAccounts />
                                  )}
                                </Private>
                              )}

                              {allowed_features.access_customers_page && (
                                <Private path="/customer/:id">
                                  <CustomerProvider>
                                    <IntercomProvider>
                                      <Page.Customer />
                                    </IntercomProvider>
                                  </CustomerProvider>
                                </Private>
                              )}

                              {allowed_features.access_rithmic_account_creation_tool && (
                                <Private path="/rithmic-account-creation-tool">
                                  {allowed_features.access_rithmic_account_creation_tool && (
                                    <Page.RithmicAccountCreationTool />
                                  )}
                                </Private>
                              )}

                              {allowed_features.access_to_compliance_search_page && (
                                <Private path="/compliance-search">
                                  <ComplianceSearchProvider>
                                    <Page.ComplianceSearch />
                                  </ComplianceSearchProvider>
                                </Private>
                              )}

                              {allowed_features.access_rithmic_sftp_server && (
                                <Private path="/rithmic-files">
                                  <Page.RithmicFiles />
                                </Private>
                              )}

                              {allowed_features.access_to_passers_page && (
                                <Private exact path="/passers">
                                  <Page.Passers />
                                </Private>
                              )}

                              {/*<Private path="*">*/}
                              {/*  <span>Page not found</span>*/}
                              {/*</Private>*/}
                            </Switch>
                          </FeedProvider>
                        </RithmicAccountsProvider>
                      </DiscountCodesLogsProvider>
                    </AgentsLogsHistoryProvider>
                  </DiscountsProvider>
                </SearchCustomersProvider>
              </PassersProvider>
            </ErrorBoundary>
          </Box>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
